import dayjs from 'dayjs'
import type { SerializableHead } from 'unhead/types'

export type LiveAgentButtonType = {
    liveAgentId: string
    elementId: string
}

const LIVE_AGENT_SCRIPT_ID = 'la_x2s6df8d'
const LIVE_AGENT_SCRIPT_SRC = 'https://help.leaf.academy/scripts/track.js'

let liveAgentScriptPromise: Promise<void> | null = null

/**
 * Load the LiveAgent tracker exactly once per page, no matter how many
 * components ask for a button.
 */
function loadLiveAgentScript(): Promise<void> {
    if (import.meta.server) return Promise.resolve()

    if (!liveAgentScriptPromise) {
        liveAgentScriptPromise = new Promise<void>((resolve, reject) => {
            const existing = document.getElementById(LIVE_AGENT_SCRIPT_ID) as HTMLScriptElement | null

            if (existing) {
                if ((window as any).LiveAgent) {
                    resolve()
                } else {
                    existing.addEventListener('load', () => resolve())
                    existing.addEventListener('error', () => reject(new Error('LiveAgent script failed to load')))
                }
                return
            }

            const script = document.createElement('script')
            script.id = LIVE_AGENT_SCRIPT_ID
            script.async = true
            script.src = LIVE_AGENT_SCRIPT_SRC
            script.onload = () => resolve()
            script.onerror = () => reject(new Error('LiveAgent script failed to load'))
            document.body.appendChild(script)
        }).catch((error) => {
            // Allow a later attempt to retry instead of caching the failure forever
            liveAgentScriptPromise = null
            throw error
        })
    }

    return liveAgentScriptPromise
}

/**
 * Composable for LiveAgent functionality
 */
export function useLiveAgent() {
    /**
     * Load LiveAgent (once) and turn the given element into a LiveAgent button.
     * Safe to call repeatedly - LiveAgent skips elements it already decorated.
     */
    const createLiveAgentButton = async (liveAgentId: string, elementId: string): Promise<void> => {
        if (import.meta.server) return

        try {
            await loadLiveAgentScript()
        } catch (error) {
            console.warn('LiveAgent unavailable:', error)
            return
        }

        const element = document.getElementById(elementId)
        if (!element) return

        ;(window as any).LiveAgent?.createButton(liveAgentId, element)
    }

    /**
     * Generate LiveAgent button script
     */
    const getLiveAgentButton = (liveAgentId: string, elementId: string): string => {
        return `(function(){var el=document.getElementById("${elementId}");if(el)LiveAgent.createButton('${liveAgentId}',el);})();`
    }

    /**
     * Add a LiveAgent button script to the page
     */
    const liveAgentAddButtonScript = (liveAgentId: string, elementId: string, id: string): SerializableHead => {
        return {
            script: [
                {
                    key: `liveagent-${id}-${dayjs().valueOf()}`,
                    innerHTML: getLiveAgentButton(liveAgentId, elementId),
                    type: 'text/javascript'
                }
            ]
        }
    }

    /**
     * Add LiveAgent head script with buttons
     */
    const liveAgentHeadScript = (buttons: LiveAgentButtonType | LiveAgentButtonType[]): SerializableHead => {
        if (!Array.isArray(buttons)) {
            buttons = [buttons]
        }

        return {
            script: [
                {
                    key: `liveagent-${dayjs().valueOf()}`,
                    innerHTML:
                        `(function(d, src, c) { var t=d.scripts[d.scripts.length - 1],s=d.createElement('script');s.id='la_x2s6df8d';s.async=true;s.src=src;s.onload=s.onreadystatechange=function(){var rs=this.readyState;if(rs&&(rs!='complete')&&(rs!='loaded')){return;}c(this);};t.parentElement.insertBefore(s,t.nextSibling);})(document,
'https://help.leaf.academy/scripts/track.js',
function(e){` +
                        buttons
                            .map((x) => {
                                return getLiveAgentButton(x.liveAgentId, x.elementId)
                            })
                            .join(' ') +
                        `});`,
                    type: 'text/javascript'
                }
            ]
        }
    }

    return {
        createLiveAgentButton,
        getLiveAgentButton,
        liveAgentAddButtonScript,
        liveAgentHeadScript
    }
}
